import moment from 'moment'

import { ConfigItem, DefaultFile } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

import { Contact as ContactNamespace } from './Contact'
import { Miscellaneous } from './Miscellaneous'

export enum DocumentTypeStatus {
	Active = 'Active',
	Inactive = 'Inactive',
	Pending = 'Pending',
	Rejected = 'Rejected'
}

export enum SupplierLocationFilterFields {
	supplierTypeId = 'supplierTypeId',
	statusId = 'statusId',
	includeInactive = 'includeInactive',
	isDataTrackingOnly = 'isDataTrackingOnly',
	isAdminOnlySupplier = 'isAdminOnlySupplier',
	notMappedToPS = 'notMappedToPS'
}

export enum SupplierLocationLabels {
	VendorId = 'vendorId',
	VendorLocation = 'vendorLocation',
	SupplierId = 'supplierId',
	Name = 'name',
	CorporateEntityTypeId = 'corporateEntityTypeId',
	GreatPlainsPaymentClassCode = 'greatPlainsPaymentClassCode',
	InvoiceDifficultyLevelCode = 'invoiceDifficultyLevelCode',
	FacilityTypeIds = 'facilityTypeIds',
	StatusId = 'statusId',
	OrderDispatchWeb = 'orderDispatchWeb',
	OrderDispatchEmail = 'orderDispatchEmail',
	OrderDispatchManual = 'orderDispatchManual',
	OrderDispatch = 'orderDispatch',
	StatusComments = 'statusComments',
	IsActive = 'isActive',
	IsCustomerIndemnified = 'isCustomerIndemnified',
	IsMinoritySupplier = 'isMinoritySupplier',
	IsTax1099Supplier = 'isTax1099Supplier',
	IsAdminOnlySupplier = 'isAdminOnlySupplier',
	IsBillingAgentOnly = 'isBillingAgentOnly',
	IsDataTrackingOnly = 'isDataTrackingOnly',
	NameInNativeLanguage = 'nameInNativeLanguage',
	ExpirationDate = 'expirationDate',
	LastSiteVisitDate = 'lastSiteVisitDate',
	DefaultGLAccount = 'defaultGLAccount',
	DefaultAffiliateCode = 'defaultAffiliateCode',
	AdminApprovalContactId = 'adminApprovalContactId',
	Tax1099VendorTypeId = 'tax1099VendorTypeId',
	Tax1099BoxNumberId = 'tax1099BoxNumberId',
	TaxNumber = 'taxNumber',
	LegalName = 'legalName',
	OrderAcceptanceHours = 'orderAcceptanceHours',
	QualityRating = 'qualityRating',
	SchedFlexRating = 'schedFlexRating',
	UnitCostTolerancePercentage = 'unitCostTolerancePercentage',
	VolumeTolerancePercentage = 'volumeTolerancePercentage',
	ServiceOccurrenceTolerancePercentage = 'serviceOccurrenceTolerancePercentage',
	SuplLocRefCode = 'suplLocRefCode',
	InvoicePaymentComments = 'invoicePaymentComments',
	Comments = 'comments',
	PaymentTerms = 'paymentTerms',
	ClassCode = 'classCode',
	RemitAddress = 'remitAddress',
	EftDocumentValue = 'EftDocument.Value',
	EftDocumentHasValue = 'EftDocument.HasValue',
	BankCountryId = 'bankCountryId',
	BankKey = 'bankKey',
	BankAccount = 'bankAccount',
	Iban = 'iban',
	BankAccountOwner = 'bankAccountOwner',
	VatOffice = 'vatOffice',
	VendorGroupCode = 'vendorGroupCode',
	statementComments = 'statementComments'
}

export enum ContactLabels {
	ContactId = 'contactId',
	Email = 'email',
	Comment = 'comment',
	OfficePhone = 'officePhone',
	MobilePhone = 'mobilePhone',
	IsActive = 'isActive',
	IsDispatch = 'isDispatch',
	IsOrder = 'isOrder',
	IsAudit = 'isAudit',
	IsSales = 'isSales',
	IsContract = 'isContract',
	EmployerName = 'employerName',
	IsInsurance = 'isInsurance',
	IsStatementContact = 'isStatementContact'
}

export type SelectLabels =
	| SupplierLocationLabels.CorporateEntityTypeId
	| SupplierLocationLabels.GreatPlainsPaymentClassCode
	| SupplierLocationLabels.InvoiceDifficultyLevelCode
	| SupplierLocationLabels.FacilityTypeIds
	| SupplierLocationLabels.StatusId

export enum SupplierLocationSearch {
	stateProvinces = 'stateProvinces',
	cityNames = 'cityNames'
}

export enum SupplierLocationSearchResultFields {
	supplierLocationIds = 'supplierLocationIds',
	supplierLocationNames = 'supplierLocationNames',
	serviceSiteIds = 'serviceSiteIds',
	serviceSiteNames = 'serviceSiteNames',
	supplierIds = 'supplierIds',
	supplierNames = 'supplierNames',
	vendorIds = 'vendorIds',
	vendorLocations = 'vendorLocations'
}

export interface SupplierLocationSearchResult {
	[SupplierLocationSearchResultFields.vendorIds]: string[]
	[SupplierLocationSearchResultFields.vendorLocations]: string[]
	[SupplierLocationSearchResultFields.supplierNames]: string[]
	[SupplierLocationSearchResultFields.serviceSiteIds]: number[]
	[SupplierLocationSearchResultFields.serviceSiteNames]: string[]
	[SupplierLocationSearchResultFields.supplierLocationIds]: number[]
	[SupplierLocationSearchResultFields.supplierLocationNames]: string[]
}

export type Location = {
	city: string | null
	province: Entity | null
	stateCode: string | null
	address: string | null
}

export declare namespace SupplierLocation {
	interface Basic {
		id: number
		supplierId: number
		supplierName: string
		supplierLocation: string
		location: Location
		company: Entity | null
		numberOfServicesLtm: number
		totalInvoiceAmountLtm: number
		isActive: true
		status: string
	}

	namespace Request {
		interface Create {
			[SupplierLocationLabels.VendorId]: string | null
			[SupplierLocationLabels.VendorLocation]: string | null
			[SupplierLocationLabels.SupplierId]: Key | null
			[SupplierLocationLabels.Name]: string
			[SupplierLocationLabels.CorporateEntityTypeId]: Key
			[SupplierLocationLabels.GreatPlainsPaymentClassCode]: string
			[SupplierLocationLabels.InvoiceDifficultyLevelCode]: string
			[SupplierLocationLabels.FacilityTypeIds]: number[]
			[SupplierLocationLabels.StatusId]: Key | null
			[SupplierLocationLabels.OrderDispatchWeb]: boolean
			[SupplierLocationLabels.OrderDispatchEmail]: boolean
			[SupplierLocationLabels.OrderDispatchManual]: boolean
			[SupplierLocationLabels.StatusComments]: string | null
			[SupplierLocationLabels.IsActive]: boolean
			[SupplierLocationLabels.IsCustomerIndemnified]: boolean
			[SupplierLocationLabels.IsMinoritySupplier]: boolean
			[SupplierLocationLabels.IsTax1099Supplier]: boolean
			[SupplierLocationLabels.IsAdminOnlySupplier]: boolean
			[SupplierLocationLabels.IsBillingAgentOnly]: boolean
			[SupplierLocationLabels.IsDataTrackingOnly]: boolean
		}

		interface Edit extends Create {
			[SupplierLocationLabels.NameInNativeLanguage]: string | null
			[SupplierLocationLabels.ExpirationDate]: string | null
			[SupplierLocationLabels.LastSiteVisitDate]: string | null
			[SupplierLocationLabels.DefaultGLAccount]: string | null
			[SupplierLocationLabels.DefaultAffiliateCode]: string | null
			[SupplierLocationLabels.AdminApprovalContactId]: Key | null
			[SupplierLocationLabels.Tax1099VendorTypeId]: Key | null
			[SupplierLocationLabels.Tax1099BoxNumberId]: Key | null
			[SupplierLocationLabels.TaxNumber]: string | null
			[SupplierLocationLabels.LegalName]: string | null
			[SupplierLocationLabels.OrderAcceptanceHours]: number | null
			[SupplierLocationLabels.QualityRating]: number | null
			[SupplierLocationLabels.SchedFlexRating]: number | null
			[SupplierLocationLabels.UnitCostTolerancePercentage]: number | null
			[SupplierLocationLabels.VolumeTolerancePercentage]: number | null
			[SupplierLocationLabels.ServiceOccurrenceTolerancePercentage]: number | null
			[SupplierLocationLabels.SuplLocRefCode]: string | null
			[SupplierLocationLabels.InvoicePaymentComments]: string | null
			[SupplierLocationLabels.Comments]: string | null
			[SupplierLocationLabels.EftDocumentValue]: File | null
			[SupplierLocationLabels.EftDocumentHasValue]: boolean
			[SupplierLocationLabels.BankCountryId]: Key | null
			[SupplierLocationLabels.BankKey]: string | null
			[SupplierLocationLabels.BankAccount]: string | null
			[SupplierLocationLabels.BankAccountOwner]: string | null
			[SupplierLocationLabels.Iban]: string | null
			[SupplierLocationLabels.VatOffice]: string | null
			[SupplierLocationLabels.VendorGroupCode]: Key | null
		}
	}

	namespace Form {
		interface AddSupplierLocation
			extends Omit<
				Request.Create,
				SupplierLocationLabels.FacilityTypeIds | SupplierLocationLabels.SupplierId
			> {
			[SupplierLocationLabels.FacilityTypeIds]: LabelValue<number>[]
			[SupplierLocationLabels.SupplierId]: LabelValue | null
		}

		interface EditSupplierLocation
			extends Omit<
				Request.Edit,
				| SupplierLocationLabels.FacilityTypeIds
				| SupplierLocationLabels.ExpirationDate
				| SupplierLocationLabels.LastSiteVisitDate
				| SupplierLocationLabels.SupplierId
				| SupplierLocationLabels.EftDocumentHasValue
				| SupplierLocationLabels.EftDocumentValue
				| SupplierLocationLabels.AdminApprovalContactId
				| SupplierLocationLabels.BankCountryId
				| SupplierLocationLabels.VendorGroupCode
			> {
			[SupplierLocationLabels.FacilityTypeIds]: LabelValue<number>[]
			[SupplierLocationLabels.ExpirationDate]: moment.Moment | null
			[SupplierLocationLabels.LastSiteVisitDate]: moment.Moment | null
			[SupplierLocationLabels.RemitAddress]: RemitAddress | null
			[SupplierLocationLabels.ClassCode]: string | null
			[SupplierLocationLabels.PaymentTerms]: PaymentTerm[]
			[SupplierLocationLabels.SupplierId]: LabelValue
			[SupplierLocationLabels.AdminApprovalContactId]: LabelValue | null
			[SupplierLocationLabels.BankCountryId]: LabelValue<number> | null
			[SupplierLocationLabels.VendorGroupCode]: LabelValue | null
			[SupplierLocationLabels.statementComments]: BasicString
		}
	}

	interface RemitAddress {
		name: string | null
		isVendorActive: boolean
		isRemitAddressActive: boolean
		addressLine: string | null
		city: string | null
		stateCode: string | null
		stateId: number
		zipCode: string | null
	}

	interface PaymentTerm {
		billingAgency: string
		paymentTerm: string
	}

	interface Details extends Entity {
		vendorId: string | null
		vendorLocation: string | null
		remitAddress: RemitAddress | null
		supplier: Entity
		nameInNativeLanguage: string | null
		corporateEntityType: Entity | null
		greatPlainsPaymentClassCode: string
		invoiceDifficultyLevelCode: string
		facilityTypes: Entity[]
		status: Entity
		orderDispatchWeb: boolean
		orderDispatchEmail: boolean
		orderDispatchManual: boolean
		statusComments: string | null
		isActive: boolean
		isCustomerIndemnified: boolean
		isMinoritySupplier: boolean
		isTax1099Supplier: boolean
		isAdminOnlySupplier: boolean
		isBillingAgentOnly: boolean
		isDataTrackingOnly: boolean
		expirationDate: string | null
		lastSiteVisitDate: string | null
		defaultGLAccount: string | null
		defaultAffiliateCode: string | null
		adminApprovalContact: Entity | null
		tax1099VendorType: Entity | null
		tax1099BoxNumber: Entity | null
		taxNumber: string | null
		legalName: string | null
		orderAcceptanceHours: number | null
		qualityRating: number | null
		schedFlexRating: number | null
		unitCostTolerancePercentage: number | null
		volumeTolerancePercentage: number | null
		serviceOccurrenceTolerancePercentage: number | null
		suplLocRefCode: string | null
		invoicePaymentComments: string | null
		comments: string | null
		classCode: string | null
		paymentTerms: PaymentTerm[]
		bankCountry: Entity | null
		bankKey: string | null
		bankAccount: string | null
		bankAccountOwner: string | null
		iban: string | null
		vatOffice: string | null
		vendorGroup: NameCode | null
		isTurkish: boolean
		statementComments: BasicString
	}

	namespace Contact {
		interface FlagFields {
			[ContactLabels.IsActive]: boolean
			[ContactLabels.IsDispatch]: boolean
			[ContactLabels.IsOrder]: boolean
			[ContactLabels.IsAudit]: boolean
			[ContactLabels.IsSales]: boolean
			[ContactLabels.IsContract]: boolean
			[ContactLabels.IsInsurance]: boolean
			[ContactLabels.IsInsurance]: boolean
			[ContactLabels.IsStatementContact]: boolean
		}

		interface Basic extends Entity, FlagFields {
			[ContactLabels.ContactId]: number
			[ContactLabels.Email]: string
			[ContactLabels.Comment]: string
			[ContactLabels.OfficePhone]: string
			[ContactLabels.MobilePhone]: string
			[ContactLabels.EmployerName]: string
		}

		namespace Request {
			interface Add extends FlagFields {
				[ContactLabels.ContactId]: string | null
				[ContactLabels.Comment]: string
			}

			interface Create
				extends Omit<ContactNamespace.Form.Create.Request, 'isActive'>,
					FlagFields {
				[ContactLabels.Comment]: string
			}
		}

		namespace Form {
			interface Add extends Omit<Request.Add, ContactLabels.ContactId> {
				[ContactLabels.ContactId]: LabelValue | null
			}

			interface Create
				extends Omit<ContactNamespace.Form.Create.Basic, 'isActive'>,
					FlagFields {
				[ContactLabels.Comment]: string
			}
		}
	}

	interface Address {
		id: number
		addressType: Entity
		addressLine1: string
		state: Miscellaneous.StateProvince
		city: string
		zip: string
		addressLine2: string | null
		county: Entity | null
		billingCurrency: Entity
		isActive: boolean
		latitude: Key | null
		longitude: Key | null
	}

	interface AddressForm extends Miscellaneous.ValidateAddressForm {
		addressTypeId: string | null
		countyId: LabelValue | null
		billingCurrencyId: string | null
		isActive: boolean
	}

	interface AddressRequest {
		addressTypeId: number
		addressLine1: string
		addressLine2: string | null
		city: string
		zip: string
		billingCurrencyId: number | null
		countyId: number | null
		stateId: number
		isActive: boolean
		latitude: Key | null
		longitude: Key | null
	}

	namespace Contract {
		interface ServiceSiteItem extends Entity {
			isAssociated: boolean
		}
		interface Basic {
			id: Key
			contractNumber: string
			startDate: string | null
			endDate: string | null
			serviceSites: number[]
		}
		interface Detailed {
			id: number
			contractNumber: string
			beginContractDate: string
			endContractDate: string
			everGreenAlertDate?: string
			sentDate?: string
			additionalInformation?: string
			internalNotes?: string
			contractFileName?: string
			associatedServiceSites?: {
				id: number
				name: string
				items: ServiceSiteItem[]
			}[]
		}
		interface Form {
			file: DefaultFile | null
			contractNumber: string | null
			startDate: moment.Moment | null
			endDate: moment.Moment | null
			everGreenAlertDate: moment.Moment | null
			sentDate: moment.Moment | null
			additionalInformation: string
			internalNotes: string
			serviceSite: LabelValue | null
			items: number[] | null
		}
		namespace Request {
			interface Create
				extends Omit<
					Detailed,
					| 'id'
					| 'serviceSites'
					| 'items'
					| 'contractFileName'
					| 'beginContractDate'
					| 'endContractDate'
				> {
				contractNumber: string
				startDate: string
				endDate: string
				serviceSiteIds?: number[] | null
				file?: File | null
			}
			interface Update extends Request.Create {
				itemIds?: number[] | null
			}
		}
	}

	namespace LocationVisit {
		type NamesData = {
			firstName: BasicString
			lastName: BasicString
		} | null

		interface EntityWithEmployer extends Entity {
			employerName: BasicString
		}

		interface Basic {
			id: number
			visitDate: BasicString
			visitParticipants: BasicString
			actionNotes: BasicString
			generalNotes: BasicString
			supplierContact: NamesData
			leadAuditor: NamesData
		}

		interface Details {
			id: number
			communicationType: Entity | null
			supplierContact: EntityWithEmployer | null
			dateOfVisit: BasicString
			leadAuditor: EntityWithEmployer | null
			meetingParticipantList: BasicString
			actionNotes: BasicString
			additionalNotes: BasicString
			generalNotes: BasicString
			document: Entity | null
		}

		namespace Form {
			type BasicLabelValue = LabelValue | null
			type BasicMoment = moment.Moment | null

			interface Basic {
				communicationType: BasicLabelValue
				supplierContact: BasicLabelValue
				visitDate: BasicMoment
				leadAuditor: BasicLabelValue
				meetingParticipantList: BasicString
				actionItemNotes: BasicString
				additionalNotes: BasicString
				generalNotes: BasicString
				file: DefaultFile | null
			}

			interface Default extends Basic {
				communicationType: null
				supplierContact: null
				leadAuditor: null
			}

			interface Valid extends Basic {
				communicationType: NonNullable<BasicLabelValue>
				supplierContact: NonNullable<BasicLabelValue>
				visitDate: NonNullable<BasicMoment>
			}
		}

		namespace Request {
			interface Basic {
				communicationTypeId: number
				supplierContactId: number
				dateOfVisit: string | null
				leadAuditorId: number | null
				meetingParticipantList: string | null
				actionNotes: string | null
				additionalNotes: string | null
				generalNotes: string | null
			}

			interface Create extends Basic {}

			interface Update extends Basic {}
		}
	}

	namespace ServiceArea {
		interface Basic {
			id: number
			stateAbbreviation: string
			stateName: string
			country: string
			notes: string
		}
		namespace Form {
			interface Create {
				region?: LabelValue | null
				state: LabelValue[]
				notes?: string
			}

			interface Edit {
				notes: string
			}
		}

		type FormData = Form.Create | Form.Edit
		interface Request {
			stateIds?: number[]
			notes?: string
		}
	}

	interface DisposalFacility extends Entity {
		isActive: boolean
		isDefault: boolean
	}

	interface Transporter extends Entity {
		isActive: boolean
	}

	interface SupplierItem extends Entity {
		chargeTypeCode: string
		cost: number | null
		units: string
		serviceSite: Entity
	}

	interface ServiceSite extends Entity {}
	interface ServiceSiteItem extends Entity {
		isAssociated: boolean
		serviceSiteId: number
	}

	namespace Search {
		interface Config {
			[SupplierLocationSearchResultFields.vendorIds]: ConfigItem<SupplierLocationSearchResultFields>
			[SupplierLocationSearchResultFields.vendorLocations]: ConfigItem<SupplierLocationSearchResultFields>
			[SupplierLocationSearchResultFields.supplierIds]: ConfigItem<SupplierLocationSearchResultFields>
			[SupplierLocationSearchResultFields.supplierNames]: ConfigItem<SupplierLocationSearchResultFields>
			[SupplierLocationSearchResultFields.serviceSiteIds]: ConfigItem<SupplierLocationSearchResultFields>
			[SupplierLocationSearchResultFields.serviceSiteNames]: ConfigItem<SupplierLocationSearchResultFields>
			[SupplierLocationSearchResultFields.supplierLocationIds]: ConfigItem<SupplierLocationSearchResultFields>
			[SupplierLocationSearchResultFields.supplierLocationNames]: ConfigItem<SupplierLocationSearchResultFields>
		}

		interface Result {
			[SupplierLocationSearchResultFields.vendorIds]: string[]
			[SupplierLocationSearchResultFields.vendorLocations]: string[]
			[SupplierLocationSearchResultFields.supplierNames]: Entity[]
			[SupplierLocationSearchResultFields.supplierIds]: Entity[]
			[SupplierLocationSearchResultFields.serviceSiteIds]: Entity[]
			[SupplierLocationSearchResultFields.serviceSiteNames]: string[]
			[SupplierLocationSearchResultFields.supplierLocationIds]: Entity[]
			[SupplierLocationSearchResultFields.supplierLocationNames]: string[]
		}

		namespace Location {
			interface Config {
				[SupplierLocationSearch.stateProvinces]: ConfigItem<SupplierLocationSearch>
				[SupplierLocationSearch.cityNames]: ConfigItem<SupplierLocationSearch>
			}

			interface Result {
				[SupplierLocationSearch.stateProvinces]: Miscellaneous.StateProvince[]
				[SupplierLocationSearch.cityNames]: string[]
			}
		}
	}

	namespace Filters {
		interface Config {
			[SupplierLocationFilterFields.supplierTypeId]: ConfigFilterItem<string, null>
			[SupplierLocationFilterFields.statusId]: ConfigFilterItem<string, string>
			[SupplierLocationFilterFields.includeInactive]: ConfigFilterItem<string, boolean>
			[SupplierLocationFilterFields.isDataTrackingOnly]: ConfigFilterItem<string, boolean>
			[SupplierLocationFilterFields.isAdminOnlySupplier]: ConfigFilterItem<string, boolean>
			[SupplierLocationFilterFields.notMappedToPS]: ConfigFilterItem<string, boolean>
		}
	}

	interface RemittanceAddress {
		id: number
		addressLine1: string
		stateCode: string
		city: string
	}

	interface InternalConcat {
		id: number
		firstName: string | null
		lastName: string | null
		employerName: string | null
	}

	namespace GenerateStatement {
		namespace Form {
			interface Generate {
				statementDateFrom: moment.Moment | null
				statementDateTo: moment.Moment | null
			}
		}
		namespace Request {
			interface Generate {
				statementDateFrom: BasicString
				statementDateTo: BasicString
			}
		}
	}

	namespace Document {
		interface Basic {
			id: number
			documentType: Entity | null
			documentName: BasicString
			lastUpdatedBy: BasicString
			lastUpdateDate: BasicString
			expirationDate: BasicString
			comment: BasicString
			documentStatus: BasicString
			hasPendingStatus: boolean
		}

		namespace Form {
			interface Add {
				documentTypeId: number
				expirationDate: BasicString
				file: DefaultFile | null
			}
		}

		namespace Request {
			interface Add {
				documentTypeId: number
				expirationDate: BasicString
				file: File | null
			}
		}
	}

	namespace DocumentType {
		interface Basic {
			id: number
			documentName: BasicString
			documentType: Entity | null
			documentStatus: BasicString
			expirationDate: BasicString
			lastUpdatedBy: BasicString
			lastUpdateDate: BasicString
			comment: BasicString
		}

		namespace Form {
			interface Edit {
				expirationDate: moment.Moment | null
				comment: BasicString
			}
		}

		namespace Request {
			interface Edit {
				expirationDate: BasicString
				comment: BasicString
				documentStatus: number
			}
		}
	}
}
